import React, { useCallback, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

import { listRooms } from '~/services/AccommodationService';

import RoomAmenitiesGrid from './RoomAmenitiesGrid';

export default function AccommodationPropertyRoomAmenitiesSection() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [expandedRoomId, setExpandedRoomId] = useState<string | false>(false);

  const {
    data: rooms = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['accommodation', 'property-rooms', propertyId],
    queryFn: async () => {
      const response = await listRooms({ propertyId: propertyId! });
      return response.result ?? [];
    },
    enabled: !!propertyId,
  });

  const handleAccordionChange = useCallback(
    (roomId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedRoomId(isExpanded ? roomId : false);
    },
    [],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Error loading rooms: {error instanceof Error ? error.message : 'Unknown error'}</Alert>
    );
  }

  if (rooms.length === 0) {
    return <Alert severity="info">No rooms found for this property. Please add rooms first.</Alert>;
  }

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="h6">Room Amenities</Typography>
      <Typography variant="body2" color="textSecondary">
        Manage amenities for each room. Expand a room to view and manage its amenities.
      </Typography>

      <Box>
        {rooms.map((room) => (
          <Accordion key={room.id} expanded={expandedRoomId === room.id} onChange={handleAccordionChange(room.id)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="medium">
                {room.name || `Room ${room.id}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <RoomAmenitiesGrid
                propertyId={propertyId!}
                roomId={room.id}
                roomName={room.name}
                isExpanded={expandedRoomId === room.id}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Stack>
  );
}
